import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import {
    ParagraphNode,
    TextNode,
    LexicalEditor,
    $getRoot,
    $insertNodes,
    EditorState,
} from 'lexical';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { ListItemNode, ListNode } from '@lexical/list';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import Theme from './components/Theme';
import ToolbarPlugin from './components/ToolbarPlugin';
import TabIndentationPlugin from './components/TabIdentationPlugin';
import { MentionNode } from './components/MentionsPlugin/MentionNode';
import MentionsPlugin, { MentionsPluginProps } from './components/MentionsPlugin/MentionsPlugin';
import './NSRichTextEditor.scss';

const editorConfig = {
    namespace: 'NSRichTextEditor',
    nodes: [
        ParagraphNode,
        TextNode,
        ListNode,
        ListItemNode,
        MentionNode,
    ],
    onError(error: Error) {
        console.error(error);
        throw error;
    },
    theme: Theme,
    editorState: (_editor: LexicalEditor) => {
        const root = $getRoot();
        if (root.getTextContent() === '') {
            const paragraph = new ParagraphNode();
            root.append(paragraph);
        }
    },
};

interface NSRichTextEditorProps {
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    onChange?: () => void;
    mentionsQueryOptions?: MentionsPluginProps['mentionsQueryOptions']
    onSelect?: MentionsPluginProps['onSelect'];
}

export type EditorContent = {
    htmlContent: string;
};

export interface NSRichTextEditorRef {
    getEditorContent: () => EditorContent;
    setEditorContent: (content: EditorContent) => void;
    isEditorEmpty: () => boolean;
    getEditorState: () => EditorState | undefined;
    clear: () => void;
}

const NSRichTextEditor = forwardRef<NSRichTextEditorRef, NSRichTextEditorProps>(({
    placeholder, className, style, onChange, mentionsQueryOptions, onSelect = () => {},
}, ref) => {
    const editorRef = useRef<LexicalEditor | null>(null);

    useImperativeHandle(ref, () => ({
        /**
         * Get the editor content as a string of HTML. (use this to save content to the API)
         * @returns The HTML content of the editor.
         */
        getEditorContent: () => {
            const currentEditor = editorRef.current;
            if (!currentEditor) {
                return {
                    htmlContent: '',
                };
            }

            let content: EditorContent = {
                htmlContent: '',
            };

            currentEditor.getEditorState().read(() => {
                content = {
                    htmlContent: $generateHtmlFromNodes(currentEditor),
                };
            });

            return content;
        },
        /**
         * Set the editor content from a string of HTML. (use this to load content from the API)
         * @param content - The HTML content to set in the editor.
         */
        setEditorContent: (content: EditorContent) => {
            const currentEditor = editorRef.current;
            if (!currentEditor) {
                return;
            }

            const parser = new DOMParser();
            const dom = parser.parseFromString(content.htmlContent, 'text/html');
            const nodes = $generateNodesFromDOM(currentEditor, dom);
            $getRoot().select();
            $insertNodes(nodes);
        },
        /**
         * Check if the editor is empty.
         * @returns True if the editor is empty, false otherwise.
         */
        isEditorEmpty: () => {
            const currentEditor = editorRef.current;
            if (!currentEditor) {
                return true;
            }

            return currentEditor.getEditorState().read(() => {
                const root = $getRoot();
                return root.getTextContent().trim() === '';
            });
        },
        /**
         * Get the Lexical editor state.
         * @returns The Lexical editor state or undefined if not initialized.
         */
        getEditorState: () => {
            const currentEditor = editorRef.current;
            if (!currentEditor) {
                return undefined;
            }
            return currentEditor.getEditorState();
        },
        /**
         * Empty the editor back to a single blank paragraph. (use this to reset
         * after a successful submit). Note: the undo history is preserved, so a
         * subsequent undo can restore the cleared content.
         */
        clear: () => {
            const currentEditor = editorRef.current;
            if (!currentEditor) {
                return;
            }
            currentEditor.update(() => {
                const root = $getRoot();
                root.clear();
                root.append(new ParagraphNode());
            });
        },
    }));

    return (
        <LexicalComposer initialConfig={editorConfig}>
            <EditorRefPlugin editorRef={editorRef} />
            <div
                className={`NSRichTextEditor w-100 h-100 mb-2 mx-auto rounded text-white ${className || ''}`}
                style={style}
            >
                <ToolbarPlugin />
                <div className="bg-transparent position-relative NSRichTextEditor__content-wrapper">
                    <RichTextPlugin
                        contentEditable={(
                            <ContentEditable
                                className="NSRichTextEditor__editor-input w-100 h-100 fs-6 text-white bg-transparent"
                                aria-placeholder={placeholder ?? 'editor'}
                                placeholder={(
                                    <div
                                        className={`
                                            text-white-50 overflow-hidden position-absolute
                                            text-truncate fs-6 user-select-none d-inline-block
                                            NSRichTextEditor__placeholder
                                        `}
                                    >
                                        {placeholder}
                                    </div>
                                )}
                            />
                        )}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <OnChangePlugin onChange={() => onChange?.()} />
                    <HistoryPlugin />
                    <AutoFocusPlugin />
                    <ListPlugin hasStrictIndent={false} />
                    <TabIndentationPlugin maxIndent={5} />
                    {mentionsQueryOptions && (
                        <MentionsPlugin
                            mentionsQueryOptions={mentionsQueryOptions}
                            onSelect={onSelect}
                        />
                    )}
                </div>
            </div>
        </LexicalComposer>
    );
});

NSRichTextEditor.displayName = 'NSRichTextEditor';

export default NSRichTextEditor;
